import { useCallback } from 'react';

import useIsOrgNavBar2025Enabled from '@/hooks/useIsOrgNavBar2025Enabled';
import { usePromiseSnackbar } from '@/hooks/usePromiseSnackbar';
import { WorkspaceSections } from '@/pages/Workspace/types';
import { BasetenPageEnum, usePageSwitcher } from '@/routes';
import useConfirmationDialog from '@/sections/Dialog/Confirmation';
import useSafeguardConfirmationDialog from '@/sections/Dialog/SafeguardConfirmation';

import {
  useDeleteTrainingJobMutation,
  useStopTrainingJobMutation,
} from './__generated__/mutations.generated';
import {
  useTrainingJobForLogsAndMetricsQuery,
  useTrainingJobQuery,
} from './__generated__/queries.generated';
import { TrainingJobQueryOptions } from './types';

function useTrainingJob(
  trainingJobId: string,
  { poll = false, skip = false }: TrainingJobQueryOptions = {},
) {
  const { data, loading } = useTrainingJobQuery({
    fetchPolicy: 'cache-first',
    variables: {
      id: trainingJobId,
    },
    pollInterval: poll ? 10000 : null,
    skip,
  });

  return {
    trainingJob: data?.trainingJob,
    loading,
  };
}

function useStopTrainingJob(id: string) {
  const promiseSnackbar = usePromiseSnackbar();
  const confirmationDialog = useConfirmationDialog();

  const [stopTrainingJob] = useStopTrainingJobMutation();

  return useCallback(async () => {
    const failedText = `An unexpected error occurred. Training job '${id}' could not be stopped.`;
    const variables = { id };

    const confirmed = await confirmationDialog({
      title: 'Stop training job',
      okText: 'Stop',
      prompt: 'Are you sure you want to stop this Training Job? This cannot be undone.',
    });

    if (confirmed) {
      promiseSnackbar(
        stopTrainingJob({
          variables,
        }),
        'The Training Job was successfully stopped.',
        failedText,
      );
    }
  }, [promiseSnackbar, id, confirmationDialog, stopTrainingJob]);
}

function useDeleteTrainingJob(
  id: string,
  name: string,
  doNotRedirect?: boolean,
  refetchQueries?: string[],
) {
  const safeguardConfirmationDialog = useSafeguardConfirmationDialog();
  const isOrgNavBar2025Enabled = useIsOrgNavBar2025Enabled();
  const promiseSnackbar = usePromiseSnackbar();
  const goToPage = usePageSwitcher();

  const [deleteTrainingJob] = useDeleteTrainingJobMutation();

  return useCallback(async () => {
    if (!name) {
      return;
    }

    const failedText = `An unexpected error occurred. Training job '${id}' could not be deleted.`;
    const variables = { id };

    const confirmed = await safeguardConfirmationDialog({
      title: 'Delete Training Job',
      okText: 'Delete',
      prompt: 'Are you sure you want to delete this Training Job? This cannot be undone.',
      validationText: name,
      validationHint: 'Training Job name',
    });

    if (confirmed) {
      promiseSnackbar(
        deleteTrainingJob({
          variables,
          refetchQueries,
          onCompleted() {
            if (!doNotRedirect) {
              goToPage(BasetenPageEnum.Workspace, {
                section: isOrgNavBar2025Enabled
                  ? WorkspaceSections.Overview
                  : WorkspaceSections.Models,
              });
            }
          },
          awaitRefetchQueries: true,
        }),
        'The Training Job was successfully deleted.',
        failedText,
      );
    }
  }, [
    promiseSnackbar,
    id,
    name,
    safeguardConfirmationDialog,
    deleteTrainingJob,
    goToPage,
    isOrgNavBar2025Enabled,
    doNotRedirect,
    refetchQueries,
  ]);
}

function useTrainingJobForLogsAndMetrics(trainingJobId: string, skip: boolean = false) {
  const { data, loading } = useTrainingJobForLogsAndMetricsQuery({
    variables: { id: trainingJobId },
    skip: skip || !trainingJobId,
    fetchPolicy: 'cache-first',
  });

  return { trainingJob: data?.trainingJob, loading };
}

export {
  useTrainingJob,
  useStopTrainingJob,
  useDeleteTrainingJob,
  useTrainingJobForLogsAndMetrics,
};
